import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { DataGrid } from "@mui/x-data-grid";
import { IconButton, Typography, Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import LaunchIcon from "@mui/icons-material/Launch";
import backArrowIcon from "../../../assets/images/back-arrow.svg";
import DeleteIcon from "../../../assets/images/delete-button-icon.svg";
import closeIcon from "../../../assets/images/close_icon.svg";
import ChevronRightIcon from "../../../assets/images/chevron-right.svg";
import Popup from "../../../shared/components/Popup/Popup";
import styles from "./GradeFilesTable.module.css";
import useOmrUploadData from "../../../store/omrUpload/useOmrUploadData";

const NotificationPortal = ({ children }) => {
  return ReactDOM.createPortal(children, document.body);
};

const GradeFilesTable = ({
  files,
  grade,
  onBack,
  onRefresh,
  paginationModel,
  onPaginationChange,
  rowCount = 0,
  isLoading = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [showNotification, setShowNotification] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [notificationData, setNotificationData] = useState({
    action: "",
    details: "",
  });
  const hasFiles = Array.isArray(files) && files.length > 0;
  const currentPaginationModel = paginationModel || { page: 0, pageSize: 10 };
  const handlePaginationChange = onPaginationChange || (() => {});
  const { deleteOmrUpload, deleteState } = useOmrUploadData({
    fetchUploads: false,
  });
  const isDeleting = deleteState.isLoading;

  const handleDeleteClick = (file) => {
    setFileToDelete(file);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!fileToDelete) return;

    try {
      await deleteOmrUpload(fileToDelete.jobId);
      setDeleteDialogOpen(false);
      setNotificationData({
        action: "Delete",
        details: `"${fileToDelete.fileName}" has been deleted successfully.`,
      });
      setShowErrorNotification(false);
      setShowNotification(true);
      setFileToDelete(null);
      onRefresh?.();
    } catch (error) {
      console.error("Error deleting file:", error);
      setNotificationData({
        action: "Delete",
        details:
          error?.data?.message ||
          error?.message ||
          "Unable to delete the selected file.",
      });
      setShowNotification(false);
      setShowErrorNotification(true);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setFileToDelete(null);
  };

  const getUploadStatusStyle = (status) => {
    const statusStyles = {
      "SQS-Queued": {
        backgroundColor: "#FFD9A8",
      },
      "upload-successful": {
        backgroundColor: "#B1F3BA",
      },
      Failed: {
        backgroundColor: "#FFC9C9",
      },
    };

    return (
      statusStyles[status] || {
        backgroundColor: "#E6E6E6",
      }
    );
  };

  const columns = [
    {
      field: "actions",
      headerName: "",
      width: 60,
      headerAlign: "center",
      align: "left",
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <IconButton
          onClick={() => handleDeleteClick(params.row)}
          size="small"
          aria-label="Delete"
          className={styles["delete-icon-button"]}
        >
          <img
            src={DeleteIcon}
            alt="Delete"
            className={styles["delete-icon"]}
          />
        </IconButton>
      ),
    },
    {
      field: "fileName",
      headerName: "File Name",
      flex: isMobile ? 2 : 1.5,
      minWidth: isMobile ? 200 : 240,
      headerAlign: "left",
      align: "left",
      disableColumnMenu: true,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontSize: "inherit",
            textAlign: "left",
          }}
          title={params.value}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: "link",
      headerName: "Link",
      flex: isMobile ? 1.3 : 1,
      minWidth: isMobile ? 100 : 120,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      headerAlign: "left",
      align: "left",
      renderCell: (params) => (
        <button
          type="button"
          className={styles["view-link-button"]}
          onClick={(event) => {
            event.stopPropagation();
            if (params.value) {
              window.open(params.value, "_blank", "noopener,noreferrer");
            }
          }}
          disabled={!params.value}
        >
          <p>View</p>
          <LaunchIcon fontSize="small" />
        </button>
      ),
    },
    {
      field: "uploadedAt",
      headerName: "Uploaded At",
      width: 250,
      disableColumnMenu: true,
      headerAlign: "left",
      align: "left",
      renderCell: (params) => {
        const displayValue =
          params.row?.uploadedAtFormatted || params.value || "-";
        return (
          <Typography
            variant="body2"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontSize: "inherit",
              textAlign: "center",
            }}
          >
            {displayValue}
          </Typography>
        );
      },
    },
    {
      field: "uploadedBy",
      headerName: "Uploaded By",
      width: 200,
      disableColumnMenu: true,
      headerAlign: "left",
      align: "left",
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontSize: "inherit",
            textAlign: "left",
          }}
          title={params.value}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: "uploadStatus",
      headerName: "Upload Status",
      width: 180,
      disableColumnMenu: true,
      headerAlign: "left",
      align: "left",
      renderCell: (params) => {
        const statusStyle = getUploadStatusStyle(params.value);
        return (
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "6px 12px",
              borderRadius: "999px",
              minWidth: "120px",
              backgroundColor: statusStyle.backgroundColor,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                whiteSpace: "nowrap",
                textAlign: "center",
                fontSize: isMobile ? "11px" : "14px",
              }}
            >
              {params.value || "-"}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "message",
      headerName: "Message",
      flex: isMobile ? 1.6 : 1.2,
      minWidth: isMobile ? 220 : 260,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      headerAlign: "left",
      align: "left",
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            overflow: "hidden",
            textOverflow: isMobile ? "clip" : "ellipsis",
            whiteSpace: isMobile ? "normal" : "nowrap",
            display: "block",
            overflowWrap: "anywhere",
            lineHeight: 1.4,
            fontSize: "inherit",
            textAlign: "left",
          }}
          title={params.value}
        >
          {params.value}
        </Typography>
      ),
    },
  ];

  const handleCloseNotification = () => {
    setShowNotification(false);
  };

  const handleCloseErrorNotification = () => {
    setShowErrorNotification(false);
  };

  useEffect(() => {
    let timer;
    if (showNotification) {
      timer = setTimeout(() => {
        setShowNotification(false);
      }, 5000);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [showNotification]);

  useEffect(() => {
    let timer;
    if (showErrorNotification) {
      timer = setTimeout(() => {
        setShowErrorNotification(false);
      }, 5000);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [showErrorNotification]);

  return (
    <div className={styles["grade-files-table-container"]}>
      <Box className={styles["grade-files-header"]}>
        <div className={styles["grade-files-breadcrumb"]}>
          <button
            type="button"
            onClick={onBack}
            className={styles["breadcrumb-button"]}
            aria-label="View all grades"
          >
            <img src={backArrowIcon} alt="Back" />
            All Grades
          </button>
          <img
            src={ChevronRightIcon}
            alt=""
            className={styles["breadcrumb-separator"]}
            aria-hidden="true"
          />
          <p className={styles["breadcrumb-current"]}>Grade {grade}</p>
        </div>
      </Box>
      <div className={styles["grade-files-table-wrapper"]}>
        {hasFiles ? (
          <div className={styles["grade-files-table-scroll"]}>
            <DataGrid
              rows={files || []}
              columns={columns}
              getRowId={(row) => row.id}
              disableRowSelectionOnClick
              loading={isLoading}
              pageSizeOptions={[10, 25, 50, 100]}
              density={isMobile ? "compact" : "standard"}
              rowHeight={isMobile ? 76 : undefined}
              columnHeaderHeight={isMobile ? 56 : undefined}
              paginationMode="server"
              paginationModel={currentPaginationModel}
              onPaginationModelChange={handlePaginationChange}
              rowCount={rowCount}
              sx={{
                minWidth: isMobile ? "580px" : "100%",
                border: "none",
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "#cce5ff",
                  borderTopLeftRadius: "12px",
                  borderTopRightRadius: "12px",
                  position: "sticky",
                  top: 0,
                  zIndex: 2,
                },
                "& .MuiDataGrid-columnHeader": {
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: "#cce5ff",
                  color: "#273743",
                  fontSize: isMobile ? "12px" : "14px",
                  padding: isMobile ? "8px 12px" : "12px 16px",
                },
                "& .MuiDataGrid-cell": {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "flex",
                  alignItems: "center",
                  textAlign: "left",
                  fontSize: isMobile ? "12px" : "14px",
                },
                "& .MuiDataGrid-row": {
                  maxHeight: "none !important",
                },
                "& .MuiDataGrid-columnSeparator": {
                  position: "relative",
                  width: "10px",
                  visibility: "visible",
                  "& svg": {
                    display: "none",
                  },
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    left: "50%",
                    top: "14%",
                    bottom: "14%",
                    width: "2px",
                    height: "50%",
                    backgroundColor: "rgba(0, 0, 0, 0.18)",
                    borderRadius: "1.5px",
                    transform: "translateY(25%)",
                  },
                },
                "& .MuiDataGrid-footerContainer": {
                  borderTop: "1px solid rgba(0, 0, 0, 0.12)",
                  padding: isMobile ? "0 4px" : "0 16px",
                  minHeight: isMobile ? "52px" : "56px",
                  ...(isMobile && {
                    position: "sticky",
                    left: 0,
                    width: "calc(100vw - 32px)",
                    maxWidth: "calc(100vw - 32px)",
                    backgroundColor: "#fff",
                  }),
                },
                "& .MuiTablePagination-root": {
                  fontSize: isMobile ? "12px" : "14px",
                  overflow: "visible",
                },
                "& .MuiTablePagination-toolbar": {
                  minHeight: isMobile ? "48px" : "52px",
                  padding: isMobile ? "0" : "0 8px",
                  flexWrap: isMobile ? "wrap" : "nowrap",
                  justifyContent: isMobile ? "flex-end" : "flex-end",
                  gap: isMobile ? "4px" : "0",
                },
                "& .MuiTablePagination-selectLabel": {
                  display: "block !important",
                  fontSize: isMobile ? "10px" : "14px",
                  marginRight: isMobile ? "2px" : "8px",
                },
                "& .MuiInputBase-root.MuiTablePagination-input": {
                  display: "inline-flex !important",
                  marginRight: isMobile ? "4px" : "16px",
                  marginLeft: isMobile ? "2px" : "8px",
                },
                "& .MuiTablePagination-select": {
                  fontSize: isMobile ? "10px" : "14px",
                  paddingRight: isMobile ? "16px" : "24px",
                  paddingLeft: isMobile ? "4px" : "8px",
                },
                "& .MuiTablePagination-displayedRows": {
                  fontSize: isMobile ? "10px" : "14px",
                  margin: isMobile ? "0 2px" : "0 8px",
                },
                "& .MuiTablePagination-actions": {
                  marginLeft: isMobile ? "2px" : "8px",
                  "& .MuiIconButton-root": {
                    padding: isMobile ? "4px" : "8px",
                  },
                },
              }}
            />
          </div>
        ) : (
          <div className={styles["grade-files-empty"]}>No PDFs to display</div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <Popup
        isVisible={deleteDialogOpen}
        onClose={handleDeleteCancel}
        title="Warning!"
        handelPrimery={handleDeleteConfirm}
        handelSecondry={handleDeleteCancel}
        FirstBtnTitle="No"
        SecondBtnTitle={isDeleting ? "Deleting..." : "Yes"}
        primaryButtonDisabled={isDeleting}
        isWarning
        contentClassName={`warning-popup__content ${styles["delete-popup-content"]}`}
      >
        <p className={styles["delete-popup-message"]}>
          Are you sure you want to delete "
          <span className={styles["delete-popup-file-name"]}>
            {fileToDelete?.fileName || "this file"}
          </span>
          "? This action cannot be undone.
        </p>
      </Popup>

      {showNotification && (
        <NotificationPortal>
          <div
            className={`${styles.notification} ${styles["notification--success"]}`}
          >
            <button
              type="button"
              onClick={handleCloseNotification}
              className={styles["notification__close-button"]}
              aria-label="Dismiss success notification"
            >
              <img
                src={closeIcon}
                alt=""
                className={styles["notification__close-icon"]}
              />
            </button>
            <h3
              className={`${styles["notification__title"]} ${styles["notification__title--success"]}`}
            >
              {notificationData.action} Successful
            </h3>
            <p className={styles["notification__message"]}>
              {notificationData.details}
            </p>
          </div>
        </NotificationPortal>
      )}

      {showErrorNotification && (
        <NotificationPortal>
          <div
            className={`${styles.notification} ${styles["notification--error"]}`}
          >
            <button
              type="button"
              onClick={handleCloseErrorNotification}
              className={styles["notification__close-button"]}
              aria-label="Dismiss error notification"
            >
              <img
                src={closeIcon}
                alt=""
                className={styles["notification__close-icon"]}
              />
            </button>
            <h3
              className={`${styles["notification__title"]} ${styles["notification__title--error"]}`}
            >
              {notificationData.action} Failed
            </h3>
            <p className={styles["notification__message"]}>
              {notificationData.details}
            </p>
          </div>
        </NotificationPortal>
      )}
    </div>
  );
};

export default GradeFilesTable;
